/**
 * Formateadores compartidos por las vistas del panel de super administración.
 */

/**
 * Clasifica una suscripción por días restantes para pintar la urgencia.
 * @param {number|null} daysRemaining
 * @returns {{ level: 'lapsed'|'critical'|'warning'|'ok', color: string, label: string }}
 */
export const expirySeverity = (daysRemaining) => {
  if (daysRemaining === null || daysRemaining === undefined) {
    return { level: 'ok', color: 'grey', label: 'Sin fecha' };
  }
  if (daysRemaining < 0) {
    return { level: 'lapsed', color: 'red', label: `Vencida hace ${Math.abs(daysRemaining)} d` };
  }
  if (daysRemaining <= 7) {
    return { level: 'critical', color: 'red', label: `${daysRemaining} d` };
  }
  if (daysRemaining <= 15) {
    return { level: 'warning', color: 'orange', label: `${daysRemaining} d` };
  }
  return { level: 'ok', color: 'green', label: `${daysRemaining} d` };
};

/**
 * Duración legible en español a partir de segundos (uptime de API y MySQL).
 * @param {number} seconds
 */
export const formatUptime = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const total = Number(seconds);
  if (!Number.isFinite(total) || total < 0) return '—';

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes > 0) return `${minutes} min`;
  return `${Math.floor(total)} s`;
};

/**
 * Tamaño en MB expresado en la unidad más legible.
 * @param {number|null} megabytes
 */
export const formatSize = (megabytes) => {
  if (megabytes === null || megabytes === undefined) return '—';
  const value = Number(megabytes);
  if (!Number.isFinite(value)) return '—';
  if (value >= 1024) return `${(value / 1024).toFixed(2)} GB`;
  if (value < 1) return `${(value * 1024).toFixed(0)} KB`;
  return `${value.toFixed(1)} MB`;
};

/**
 * Fecha corta local. Devuelve guion largo cuando no hay dato.
 * @param {string|Date|null} value
 * @param {boolean} [withTime]
 */
export const formatDate = (value, withTime = false) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

/**
 * Importe con separadores de miles y símbolo de moneda.
 * @param {number} amount
 * @param {string} [currency]
 */
export const formatCurrency = (amount, currency = 'MXN') => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Etiqueta legible del mes `YYYY-MM` usado en las series de altas.
 * @param {string} month
 */
export const formatMonth = (month) => {
  if (!month) return '—';
  const [year, monthNumber] = String(month).split('-');
  const date = new Date(Number(year), Number(monthNumber) - 1, 1);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
};

export default {
  expirySeverity,
  formatUptime,
  formatSize,
  formatDate,
  formatCurrency,
  formatMonth,
};
